<div class="min-h-screen bg-gray-100 p-6">
  <div class="w-full mx-auto bg-white rounded-xl shadow-md p-6">

    <!-- Header -->
    <div class="flex justify-between items-center mb-5">
      <h1 class="text-2xl font-bold text-gray-900 leading-tight">Add Expense</h1>

      <a [routerLink]="listPath" class="block cursor-pointer capitalize w-[100px] lg:w-[150px] p-1 md:p-1.5 text-base xl:text-lg font-bold 
                text-white bg-c19725 border border-c19725 hover:bg-fdffc6 hover:text-c19725 
                hover:shadow-lg rounded-md text-center">
        Expense List
      </a>
    </div>

    <!-- Expense Form -->
    <form [formGroup]="expenseForm" class="space-y-6" (keydown)="handleKeydown($event)">

      <!-- Row -->
      <div class="grid grid-cols-1 md:grid-cols-3 gap-6">

        <!-- Expense Date -->
        <div>
          <label class="block text-gray-700 font-medium mb-1">Date</label>
          <input type="date" formControlName="exp_date" (keydown.enter)="focusNext($event)" class="w-full border border-gray-300 rounded-lg px-4 py-2 
               focus:ring-2 focus:ring-c19725 focus:outline-none" />
        </div>

        <!-- Expense Type (Dropdown) -->
        <div class="flex flex-col">
          <label class="text-xs font-bold text-gray-700 uppercase tracking-wider mb-2">Expense Type</label>
          <select formControlName="exp_type_id"
            class="p-3 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#c19725]/50 outline-none transition-all shadow-sm text-sm appearance-none cursor-pointer">
            <option value="">Select Expense Type</option>
            <option *ngFor="let item of expenseAccounts" [value]="item.id">
              {{ item.exty_name }}
            </option>
          </select>
          <p *ngIf="expenseAccounts.length === 0" class="text-[10px] text-red-400 mt-1 uppercase">
            No types found. Create them in Admin Panel.
          </p>
        </div>

        <!-- Expense Amount -->
        <div>
          <label class="block text-gray-700 font-medium mb-1">Amount</label>
          <input type="number" formControlName="exp_amount" (keydown.enter)="focusNext($event)"
            placeholder="Enter Amount" class="no-spinner w-full border border-gray-300 rounded-lg px-4 py-2 
               focus:ring-2 focus:ring-c19725 focus:outline-none" />
        </div>

      </div>

      <!-- File Upload Section -->
      <div class="mt-6 border-t border-gray-100 pt-6">

        <!-- Existing Document Preview -->
        <div *ngIf="currentDocumentUrl && !fileName"
          class="mb-4 flex items-center gap-4 bg-gray-50/50 p-3 rounded-xl border border-dotted border-c19725/50">
          <div
            class="w-16 h-16 rounded-lg overflow-hidden border border-gray-200 shadow-sm bg-white cursor-pointer hover:scale-105 transition-transform"
            (click)="openDocument(currentDocumentUrl)" title="Click to view full document">

            <!-- Image Preview -->
            <img *ngIf="!currentDocumentUrl.toLowerCase().endsWith('.pdf')" [src]="currentDocumentUrl"
              class="w-full h-full object-cover" alt="Current Bill" />

            <!-- PDF Icon Preview -->
            <div *ngIf="currentDocumentUrl.toLowerCase().endsWith('.pdf')"
              class="w-full h-full flex items-center justify-center bg-red-100 text-red-600">
              <i class="fas fa-file-pdf text-2xl"></i>
            </div>
          </div>
          <div class="cursor-pointer" (click)="openDocument(currentDocumentUrl)">
            <p class="text-xs font-bold text-gray-700 uppercase tracking-wider">Currently Attached Bill</p>
            <p class="text-[10px] text-gray-400 mt-1 uppercase">Click to view full • Upload new to replace</p>
          </div>
        </div>

        <label class="block text-gray-700 font-medium mb-1 uppercase text-xs tracking-wider">
          {{ editMode ? 'Replace Document (Optional)' : 'Attach Document (Bill/Receipt)' }}
        </label>
        <div class="relative group w-full">
          <label
            class="flex flex-col items-center justify-center w-full min-h-[140px] border-2 border-gray-300 border-dashed rounded-2xl cursor-pointer bg-gray-50 hover:bg-fdffc6/20 transition-all shadow-sm">
            <div class="flex flex-col items-center justify-center py-5 text-center">
              <svg *ngIf="!fileName" class="w-10 h-10 text-gray-400 mb-2" fill="none" stroke="currentColor"
                viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"></path>
              </svg>
              <svg *ngIf="fileName" class="w-10 h-10 text-c19725 mb-2" fill="none" stroke="currentColor"
                viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z">
                </path>
              </svg>

              <p class="text-xs text-gray-600 font-bold px-4" *ngIf="!fileName">Click to browse file</p>
              <p class="text-xs text-c19725 font-black truncate max-w-[400px] px-6" *ngIf="fileName">{{ fileName }}</p>

              <p class="text-[9px] text-gray-400 mt-1 uppercase px-4" *ngIf="!fileName">PDF, Images, or Documents</p>
            </div>
            <input type="file" class="hidden" (change)="onFileSelected($event)" />
          </label>
        </div>
      </div>

      <!-- Submit Button -->
      <div class="flex items-center justify-center mt-4 md:mt-8 space-x-1.5 md:space-x-4">
        <button type="button" (click)="onClear()"
          class="block cursor-pointer capitalize w-[110px] lg:w-[200px] p-1 md:p-1.5 text-base xl:text-lg font-bold text-c19725 bg-white border-1 border-c19725 hover:bg-fdffc6 hover:text-c19725 hover:shadow-lg focus:ring-0 focus:outline-none rounded-md text-center">
          Clear
        </button>

        <button type="button" (click)="onSubmit()" *ngIf="!editMode"
          class="block cursor-pointer capitalize w-[110px] lg:w-[200px] p-1 md:p-1.5 text-base xl:text-lg font-bold text-white bg-c19725 border-1 border-c19725 hover:bg-fdffc6 hover:text-c19725 hover:shadow-lg focus:ring-0 focus:outline-none rounded-md text-center">
          Submit
        </button>

        <button type="button" (click)="updateExpense()" *ngIf="editMode"
          class="block cursor-pointer capitalize w-[110px] lg:w-[200px] p-1 md:p-1.5 text-base xl:text-lg font-bold text-white bg-c19725 border-1 border-c19725 hover:bg-fdffc6 hover:text-c19725 hover:shadow-lg focus:ring-0 focus:outline-none rounded-md text-center">
          Update
        </button>
      </div>

    </form>

  </div>
</div>